import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

import Select from '@/components/ui/Select';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import SectionConfigEditor from './components/SectionConfigEditor';

interface HomepageSection {
  id: string;
  section_type: string;
  title: string;
  subtitle: string;
  content: any;
  is_active: boolean;
  display_order: number;
}

interface SectionFormData {
  section_type: string;
  title: string;
  subtitle: string;
  content: any;
  is_active: boolean;
}

const SECTION_TYPES = [
  { label: 'Hero Banner', value: 'hero' },
  { label: 'Featured Products', value: 'featured_products' },
  { label: 'New Arrivals', value: 'new_arrivals' },
  { label: 'Categories', value: 'categories' },
  { label: 'Story (Editorial)', value: 'story' },
  { label: 'Image Banner', value: 'image_banner' },
  { label: 'Seasonal Spotlight', value: 'seasonal_spotlight' },
  { label: 'Value Propositions', value: 'value_props' },
  { label: 'Image Gallery', value: 'image_gallery' },
  { label: 'Banner', value: 'banner' },
  { label: 'Newsletter Signup', value: 'newsletter' },
  { label: 'Growing Tips', value: 'growing_tips' },
  { label: 'Testimonials', value: 'testimonials' },
  { label: 'Custom HTML', value: 'custom_html' },
  { label: 'App Embed', value: 'app_embed' },
  { label: 'Pricing Tiers', value: 'pricing_tiers' },
  { label: 'Course Highlight', value: 'course_highlight' },
  { label: 'Instructor Bios', value: 'instructor_bios' },
  { label: 'FAQ', value: 'faq' },
  { label: 'Stats Counter', value: 'stats_counter' },
  { label: 'Trust Badges', value: 'trust_badges' },
  { label: 'Learning Path', value: 'learning_path' },
  { label: 'Video Showcase', value: 'video_showcase' },
  { label: 'Feature Comparison', value: 'feature_comparison' },
  { label: 'CTA Banner', value: 'cta_banner' },
  { label: 'Process Steps', value: 'process_steps' },
];

const getSectionTypeBadgeVariant = (type: string): 'green' | 'yellow' | 'red' | 'blue' | 'gray' => {
  switch (type) {
    case 'hero':
      return 'blue';
    case 'featured_products':
    case 'new_arrivals':
      return 'green';
    case 'newsletter':
      return 'yellow';
    default:
      return 'gray';
  }
};

export default function HomepageBuilder() {
  const [sections, setSections] = useState<HomepageSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<HomepageSection | null>(null);
  const [sectionToDelete, setSectionToDelete] = useState<HomepageSection | null>(null);
  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState<SectionFormData>({
    section_type: '',
    title: '',
    subtitle: '',
    content: {},
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<{ sections: HomepageSection[] }>('/admin/homepage');
      setSections(response.sections.sort((a, b) => a.display_order - b.display_order));
    } catch (err) {
      setError('Failed to load homepage sections. Please try again.');
      console.error('Error fetching sections:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingSection(null);
    setFormData({
      section_type: '',
      title: '',
      subtitle: '',
      content: {},
      is_active: true,
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const handleEditClick = (section: HomepageSection) => {
    setEditingSection(section);
    setFormData({
      section_type: section.section_type,
      title: section.title,
      subtitle: section.subtitle,
      content: section.content || {},
      is_active: section.is_active,
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const handleDeleteClick = (section: HomepageSection) => {
    setSectionToDelete(section);
    setDeleteModalOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.section_type) {
      errors.section_type = 'Section type is required';
    }

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);

      if (editingSection) {
        await api.put(`/admin/homepage/${editingSection.id}`, formData);
      } else {
        await api.post('/admin/homepage', formData);
      }

      await fetchSections();
      setModalOpen(false);
      setEditingSection(null);
    } catch (err) {
      console.error('Error saving section:', err);
      alert('Failed to save section. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!sectionToDelete) return;

    try {
      setIsDeleting(true);
      await api.delete(`/admin/homepage/${sectionToDelete.id}`);
      setSections(sections.filter(s => s.id !== sectionToDelete.id));
      setDeleteModalOpen(false);
      setSectionToDelete(null);
    } catch (err) {
      console.error('Error deleting section:', err);
      alert('Failed to delete section. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleActive = async (section: HomepageSection) => {
    try {
      await api.put(`/admin/homepage/${section.id}`, {
        ...section,
        is_active: !section.is_active,
      });
      setSections(sections.map(s =>
        s.id === section.id ? { ...s, is_active: !s.is_active } : s
      ));
    } catch (err) {
      console.error('Error toggling section:', err);
      alert('Failed to update section. Please try again.');
    }
  };

  const handleReorder = async (sectionId: string, direction: 'up' | 'down') => {
    const currentIndex = sections.findIndex(s => s.id === sectionId);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === sections.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newSections = [...sections];
    const [movedSection] = newSections.splice(currentIndex, 1);
    newSections.splice(newIndex, 0, movedSection);

    const reorderedSections = newSections.map((section, index) => ({
      ...section,
      display_order: index,
    }));

    setSections(reorderedSections);

    try {
      await api.put('/admin/homepage/reorder', {
        sections: reorderedSections.map(s => ({ id: s.id, display_order: s.display_order })),
      });
    } catch (err) {
      console.error('Error reordering sections:', err);
      alert('Failed to reorder sections. Please try again.');
      fetchSections();
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingSection(null);
    setFormErrors({});
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSectionToDelete(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading homepage sections...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4" role="alert">
        <Card padding="lg">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchSections} variant="primary">
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Homepage Builder</h1>
          <p className="text-gray-600 mt-1">Design and manage your homepage sections</p>
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={handleAddClick}
          aria-label="Add new section"
        >
          <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
          Add Section
        </Button>
      </div>

      {sections.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No sections found. Create your first section to get started.</p>
            <Button variant="primary" onClick={handleAddClick}>
              <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
              Create Section
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {sections.map((section, index) => (
            <Card key={section.id} padding="lg">
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleReorder(section.id, 'up')}
                    disabled={index === 0}
                    aria-label={`Move ${section.title} up`}
                  >
                    <ChevronUp className="w-4 h-4" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleReorder(section.id, 'down')}
                    disabled={index === sections.length - 1}
                    aria-label={`Move ${section.title} down`}
                  >
                    <ChevronDown className="w-4 h-4" aria-hidden="true" />
                  </Button>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant={getSectionTypeBadgeVariant(section.section_type)}>
                      {SECTION_TYPES.find(t => t.value === section.section_type)?.label || section.section_type}
                    </Badge>
                    <Badge variant={section.is_active ? 'green' : 'gray'}>
                      {section.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {section.title}
                  </h3>
                  {section.subtitle && (
                    <p className="text-sm text-gray-600 truncate mt-1">
                      {section.subtitle}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={section.is_active}
                      onChange={() => handleToggleActive(section)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      aria-label={`Toggle ${section.title} active status`}
                    />
                    <span className="text-sm text-gray-600">Active</span>
                  </label>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditClick(section)}
                    aria-label={`Edit ${section.title}`}
                  >
                    <Edit2 className="w-4 h-4" aria-hidden="true" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(section)}
                    aria-label={`Delete ${section.title}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={handleModalClose}
        title={editingSection ? 'Edit Section' : 'Add Section'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Section Type"
            value={formData.section_type}
            onChange={(e) => setFormData({ ...formData, section_type: e.target.value })}
            options={[{ label: 'Select a section type', value: '' }, ...SECTION_TYPES]}
            error={formErrors.section_type}
            required
            aria-required="true"
          />

          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            error={formErrors.title}
            required
            aria-required="true"
          />

          <Input
            label="Subtitle"
            value={formData.subtitle}
            onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
            helperText="Optional subtitle or description"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content Configuration</label>
            <SectionConfigEditor
              sectionType={formData.section_type}
              config={formData.content || {}}
              onChange={(config) => setFormData({ ...formData, content: config })}
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-900">Active</span>
          </label>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleModalClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={saving}
              disabled={saving}
            >
              {editingSection ? 'Update Section' : 'Add Section'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        title="Delete Section"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete <strong>{sectionToDelete?.title}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              isLoading={isDeleting}
            >
              Delete Section
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
